import api from '../../services/api';
import {
  translate,
  getCurrentLanguageCode
} from '../../utils/translation';

// initial state
const state = {
  purposes: [],
  notices: {
    availability: {
      // Usa chiavi di traduzione invece di testo hardcoded
      textKey: 'notices.availability'
    },
    service: {
      textKey: 'notices.service'
    },
    error: {
      textKey: 'notices.error'
    },
    appointment_completed: {
      textKey: 'notices.appointment_completed'
    }
  },
  availableDates: [],
  availableTimes: [],
  currentAppointment: null,
  loading: false,
  error: null,
  showErrorDialog: false
};

// getters
const getters = {
  getPurposeById: state => id => {
    return state.purposes.find(purpose => purpose.id === id);
  },

  // Aggiungi getters per i testi delle notifiche con traduzione
  getNoticeText: state => noticeKey => {
    const notice = state.notices[noticeKey];
    if (!notice) return '';

    return translate(notice.textKey);
  }
};

// actions
const actions = {
  async fetchPurposes({
    commit
  }) {
    commit('setLoading', true);
    try {
      const response = await api.getPurposes(getCurrentLanguageCode());
      commit('setPurposes', response.data);
    } catch (error) {
      commit('setError', error.message);
    } finally {
      commit('setLoading', false);
    }
  },

  async fetchAvailableDates({
    commit
  }, {
    storeId,
    purposeId,
    appointmentId = null
  }) {
    commit('setLoading', true);
    try {
      const response = await api.getAvailableDates(storeId, purposeId, appointmentId);
      commit('setAvailableDates', response.data);
    } catch (error) {
      commit('setError', error.message);
    } finally {
      commit('setLoading', false);
    }
  },

  async fetchAvailableTimes({
    commit
  }, {
    storeId,
    purposeId,
    date,
    appointmentId = null
  }) {
    commit('setLoading', true);
    try {
      const response = await api.getAvailableTimes(storeId, purposeId, date, appointmentId);
      commit('setAvailableTimes', response.data);
    } catch (error) {
      commit('setError', error.message);
    } finally {
      commit('setLoading', false);
    }
  },

  async createAppointment({
    commit
  }, appointmentData) {
    commit('setLoading', true);
    try {
      const response = await api.createAppointment(appointmentData);
      commit('setCurrentAppointment', response.data);
      return response.data;
    } catch (error) {
      commit('setError', error.message);
      throw error;
    } finally {
      commit('setLoading', false);
    }
  },

  async updateAppointment({
    commit
  }, {
    id,
    token,
    data
  }) {
    commit('setLoading', true);
    try {
      const response = await api.updateAppointment(id, token, data);
      commit('setCurrentAppointment', response.data);
      return response.data;
    } catch (error) {
      commit('setError', error.message);
      throw error;
    } finally {
      commit('setLoading', false);
    }
  },

  async loadAppointment({
    commit
  }, {
    id,
    token
  }) {
    commit('setLoading', true);
    try {
      const response = await api.getAppointment(id, token);
      commit('setCurrentAppointment', response.data);
      return response.data;
    } catch (error) {
      // Gestione specifica per errori API con informazioni aggiuntive
      if (error.isApiError) {
        let errorMessageKey = 'errors.general';

        // Personalizza la chiave del messaggio in base al tipo di errore
        if (error.type === 'not_found') {
          errorMessageKey = 'errors.appointment_not_found';
        } else if (error.type === 'appointment_completed') {
          errorMessageKey = 'errors.appointment_completed';
        } else if (error.type === 'network_error') {
          errorMessageKey = 'errors.network_error';
        }

        commit('setError', translate(errorMessageKey));
        commit('setShowErrorDialog', true);
      } else {
        commit('setError', error.message || translate('errors.general'));
        commit('setShowErrorDialog', true);
      }
      throw error;
    } finally {
      commit('setLoading', false);
    }
  },

  clearError({
    commit
  }) {
    commit('setError', null);
    commit('setShowErrorDialog', false);
  },

  resetAppointmentData({
    commit
  }) {
    commit('setCurrentAppointment', null);
  },

  async setEarlierNotificationPreference({
    commit
  }, {
    appointmentId,
    enableNotification
  }) {
    commit('setLoading', true);
    try {
      const response = await api.setEarlierNotificationPreference(appointmentId, enableNotification);
      commit('setNotificationPreference', {
        appointmentId,
        enableNotification
      });
      return response.data;
    } catch (error) {
      commit('setError', error.message);
      throw error;
    } finally {
      commit('setLoading', false);
    }
  },

  async deleteAppointment({
    commit
  }, {
    id,
    token
  }) {
    commit('setLoading', true);
    try {
      const response = await api.deleteAppointment(id, token);
      commit('setCurrentAppointment', null);
      return response.data;
    } catch (error) {
      commit('setError', error.message);
      throw error;
    } finally {
      commit('setLoading', false);
    }
  }
};

// mutations
const mutations = {
  setPurposes(state, purposes) {
    state.purposes = purposes;
  },

  setAvailableDates(state, dates) {
    state.availableDates = dates;
  },

  setAvailableTimes(state, times) {
    state.availableTimes = times;
  },

  setCurrentAppointment(state, appointment) {
    state.currentAppointment = appointment;
  },

  setLoading(state, status) {
    state.loading = status;
  },

  setError(state, error) {
    state.error = error;
  },

  setShowErrorDialog(state, status) {
    state.showErrorDialog = status;
  },

  setNotificationPreference(state, {
    appointmentId,
    enableNotification
  }) {
    if (state.currentAppointment && state.currentAppointment.id === appointmentId) {
      state.currentAppointment = {
        ...state.currentAppointment,
        notify_earlier: enableNotification
      };
    }
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};